import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft, Search, Pill, ShoppingBag, GitBranch, ShieldAlert, CheckCircle2,
  Factory, Warehouse, Truck, Building2, Megaphone, AlertTriangle, Download, Plus,
  User, Calendar, Phone, MapPin, Activity, ArrowUpRight, Bell, History,
  Loader2, XCircle, ChevronDown,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  alertesStock, lots, medicaments, mouvements, dispensations,
} from "@/lib/pharmacy-data";

type TabKey = "stocks" | "dispensation" | "tracabilite" | "vue-patient";

const TABS: { key: TabKey; label: string; icon: LucideIcon }[] = [
  { key: "stocks", label: "Stocks", icon: Pill },
  { key: "dispensation", label: "Dispensation", icon: ShoppingBag },
  { key: "tracabilite", label: "Traçabilité", icon: GitBranch },
  { key: "vue-patient", label: "Vue patient", icon: User },
];

/* ----------------------------- PATIENT DATA ----------------------------- */

type PatientRec = {
  id: string;
  prenom: string;
  nom: string;
  dob: string;
  sexe: "F" | "M";
  ville: string;
  tel: string;
  groupe: string;
  allergies: string[];
  pathologies: string[];
};

const PATIENTS: PatientRec[] = [
  { id: "P-29481", prenom: "James", nom: "Miamai", dob: "1982-04-12", sexe: "M", ville: "Casablanca", tel: "+212 6 12 34 56 78", groupe: "O+", allergies: ["Pénicilline"], pathologies: ["HTA"] },
  { id: "P-30205", prenom: "Aïcha", nom: "Bennani", dob: "1991-09-03", sexe: "F", ville: "Rabat", tel: "+212 6 22 11 03 45", groupe: "A+", allergies: [], pathologies: ["Asthme"] },
  { id: "P-30112", prenom: "Karim", nom: "Ouahbi", dob: "1975-11-22", sexe: "M", ville: "Marrakech", tel: "+212 6 70 18 22 91", groupe: "B+", allergies: ["Aspirine"], pathologies: ["BPCO"] },
  { id: "P-30410", prenom: "Fatima", nom: "Zahra", dob: "1968-02-17", sexe: "F", ville: "Fès", tel: "+212 6 55 47 12 02", groupe: "AB+", allergies: [], pathologies: ["Diabète T2"] },
  { id: "P-30599", prenom: "Hassan", nom: "Alami", dob: "1959-07-30", sexe: "M", ville: "Tanger", tel: "+212 6 61 99 04 11", groupe: "O-", allergies: ["Iode"], pathologies: ["Diabète T1", "Néphropathie"] },
  { id: "P-30701", prenom: "Yasmine", nom: "Idrissi", dob: "1995-12-08", sexe: "F", ville: "Agadir", tel: "+212 6 78 33 02 19", groupe: "A-", allergies: [], pathologies: ["Migraine"] },
  { id: "P-30812", prenom: "Mohamed", nom: "Saidi", dob: "1950-05-25", sexe: "M", ville: "Oujda", tel: "+212 6 42 91 50 30", groupe: "B-", allergies: ["Codéine"], pathologies: ["Cancer (palliatif)"] },
];

function ageFrom(dob: string) {
  return Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 3600 * 1000));
}

function getLastPatientId(): string {
  const sorted = [...dispensations].sort((a, b) => b.date.localeCompare(a.date));
  return sorted[0]?.patientId ?? PATIENTS[0].id;
}

/* ------------------------------ ROOT VIEW ------------------------------- */

export default function PharmacyView({
  onBack, initialTab = "stocks",
}: { onBack: () => void; initialTab?: TabKey }) {
  const [tab, setTab] = useState<TabKey>(initialTab);
  const lastId = useMemo(getLastPatientId, []);
  const [selectedId, setSelectedId] = useState<string>(lastId);
  const selected = PATIENTS.find(p => p.id === selectedId) ?? PATIENTS[0];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Clinical Operations
        </button>
        <div className="text-xs text-muted-foreground">
          Pharmacy · Stocks · Dispensation · Traçabilité
        </div>
      </div>

      {/* Pill tabs */}
      <div className="card-surface flex w-full items-center gap-2 overflow-x-auto rounded-full p-1.5">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={
                "group inline-flex shrink-0 items-center gap-2 rounded-full px-4 h-10 text-sm font-medium transition-all " +
                (active
                  ? "bg-foreground text-background shadow-[0_6px_20px_-8px_rgba(0,0,0,0.45)]"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/5")
              }
            >
              <Icon className="h-4 w-4" strokeWidth={1.8} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Patient context strip — persists across tabs */}
      <PatientContextStrip
        selected={selected}
        lastId={lastId}
        onPick={setSelectedId}
        onGoVue={() => setTab("vue-patient")}
      />

      {tab === "stocks" && <StocksTab patient={selected} />}
      {tab === "dispensation" && <DispensationTab patient={selected} />}
      {tab === "tracabilite" && <TracabiliteTab patient={selected} />}
      {tab === "vue-patient" && (
        <VuePatientTab
          selected={selected}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          lastId={lastId}
        />
      )}
    </div>
  );
}

/* --------------------------- PATIENT STRIP ------------------------------ */

function PatientContextStrip({
  selected, lastId, onPick, onGoVue,
}: {
  selected: PatientRec;
  lastId: string;
  onPick: (id: string) => void;
  onGoVue: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-surface flex flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3">
      <div className="flex items-center gap-3">
        <Avatar name={`${selected.prenom} ${selected.nom}`} />
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Patient en contexte</div>
          <div className="text-sm font-medium">
            {selected.prenom} {selected.nom}
            <span className="ml-2 text-xs text-muted-foreground">{selected.id} · {ageFrom(selected.dob)} ans</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPick(lastId)}
          className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 h-9 text-xs text-muted-foreground hover:text-foreground"
          title="Revenir au dernier patient"
        >
          <History className="h-3.5 w-3.5" /> Dernier patient
        </button>
        <div className="relative">
          <button
            onClick={() => setOpen(o => !o)}
            className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 h-9 text-xs text-muted-foreground hover:text-foreground"
          >
            Changer <ChevronDown className="h-3.5 w-3.5" />
          </button>
          {open && (
            <div className="absolute right-0 z-20 mt-2 w-72 overflow-hidden rounded-2xl border border-white/10 bg-card shadow-xl">
              <div className="max-h-72 overflow-y-auto p-1">
                {PATIENTS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => { onPick(p.id); setOpen(false); }}
                    className={
                      "flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left text-sm hover:bg-white/5 " +
                      (p.id === selected.id ? "bg-white/[0.04]" : "")
                    }
                  >
                    <Avatar name={`${p.prenom} ${p.nom}`} />
                    <div className="min-w-0">
                      <div className="truncate font-medium">{p.prenom} {p.nom}</div>
                      <div className="text-xs text-muted-foreground">{p.id} · {p.ville}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <button
          onClick={onGoVue}
          className="inline-flex items-center gap-2 rounded-full bg-foreground px-3 h-9 text-xs font-medium text-background hover:opacity-90"
        >
          <User className="h-3.5 w-3.5" /> Vue patient
        </button>
      </div>
    </div>
  );
}

/* ------------------------------- STOCKS --------------------------------- */

function StocksTab({ patient }: { patient: PatientRec }) {
  const [q, setQ] = useState("");
  const critique = alertesStock.filter(a => a.stockActuel < a.stockSecurite * 0.5).length;
  const sousSeuil = alertesStock.filter(a => a.stockActuel < a.stockSecurite).length;

  // CIPs used by this patient — surface their lots first
  const patientCips = useMemo(
    () => new Set(dispensations.filter(d => d.patientId === patient.id).map(d => d.cip)),
    [patient.id],
  );

  const filteredLots = useMemo(() => {
    const term = q.toLowerCase();
    const base = lots.filter(l => {
      const med = medicaments.find(m => m.cip === l.cip);
      return !term || [l.numeroLot, l.cip, med?.nomCommercial, med?.dci]
        .filter(Boolean).some(s => s!.toLowerCase().includes(term));
    });
    return [...base].sort((a, b) =>
      (patientCips.has(b.cip) ? 1 : 0) - (patientCips.has(a.cip) ? 1 : 0)
    );
  }, [q, patientCips]);

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Références suivies" value={String(medicaments.length)} hint="Catalogue actif" />
        <Kpi label="Lots actifs" value={String(lots.filter(l => l.statut === "actif").length)} hint={`${lots.length} lots au total`} />
        <Kpi label="Sous seuil sécurité" value={String(sousSeuil)} hint="3 mois min." tone="warning" />
        <Kpi label="Critiques" value={String(critique)} hint="< 50% du seuil" tone="danger" />
      </div>

      <Card title="Stock de sécurité" subtitle="Surveillance des seuils réglementaires AMMPS — 3 mois de couverture minimum.">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3">Médicament</th>
                <th className="px-4 py-3">Région</th>
                <th className="px-4 py-3 text-right">Conso. mensuelle</th>
                <th className="px-4 py-3 text-right">Stock actuel</th>
                <th className="px-4 py-3 text-right">Stock sécurité</th>
                <th className="px-4 py-3 w-56">Couverture</th>
                <th className="px-4 py-3">Statut</th>
              </tr>
            </thead>
            <tbody>
              {alertesStock.map(a => {
                const ratio = Math.min(100, (a.stockActuel / a.stockSecurite) * 100);
                const critical = a.stockActuel < a.stockSecurite * 0.5;
                const warn = a.stockActuel < a.stockSecurite;
                const patientUses = patientCips.has(a.cip);
                return (
                  <tr key={a.cip} className={"border-t border-white/5 " + (patientUses ? "bg-white/[0.03]" : "")}>
                    <td className="px-4 py-3 font-medium">
                      {a.nom}
                      {patientUses && <span className="ml-2 rounded-full bg-foreground/10 px-2 py-0.5 text-[10px] text-muted-foreground">utilisé par patient</span>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{a.region}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{a.consommationMensuelle.toLocaleString("fr-FR")}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">{a.stockActuel.toLocaleString("fr-FR")}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{a.stockSecurite.toLocaleString("fr-FR")}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/5">
                          <div
                            className={"h-full " + (critical ? "bg-destructive" : warn ? "bg-warning" : "bg-success")}
                            style={{ width: `${ratio}%` }}
                          />
                        </div>
                        <span className="w-12 text-right text-xs tabular-nums text-muted-foreground">{a.joursCouverture}j</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {critical ? (
                        <Pill_ tone="danger"><ShieldAlert className="h-3 w-3" /> Critique</Pill_>
                      ) : warn ? (
                        <Pill_ tone="warning">Sous seuil</Pill_>
                      ) : (
                        <Pill_ tone="success"><CheckCircle2 className="h-3 w-3" /> Conforme</Pill_>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card
        title="Lots & Batch Management"
        subtitle={`Traçabilité unitaire — lots utilisés par ${patient.prenom} ${patient.nom} mis en avant.`}
        actions={
          <div className="relative w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Filtrer par n° lot, CIP, médicament…"
              className="h-9 w-full rounded-full bg-white/5 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/30"
            />
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3">N° Lot</th>
                <th className="px-4 py-3">Médicament</th>
                <th className="px-4 py-3">Fabricant</th>
                <th className="px-4 py-3">Fabrication</th>
                <th className="px-4 py-3">Expiration</th>
                <th className="px-4 py-3 text-right">Restant</th>
                <th className="px-4 py-3">Statut</th>
              </tr>
            </thead>
            <tbody>
              {filteredLots.map(l => {
                const med = medicaments.find(m => m.cip === l.cip)!;
                const expired = l.statut === "expiré";
                const recalled = l.statut === "rappelé";
                const pct = (l.quantiteRestante / l.quantiteInitiale) * 100;
                const isPatient = patientCips.has(l.cip);
                return (
                  <tr key={l.id} className={"border-t border-white/5 " + (isPatient ? "bg-white/[0.03]" : "")}>
                    <td className="px-4 py-3 font-mono text-xs">{l.numeroLot}</td>
                    <td className="px-4 py-3 font-medium">
                      {med.nomCommercial}
                      <div className="text-xs text-muted-foreground">{med.dosage} · {med.dci}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{l.fabricant}</td>
                    <td className="px-4 py-3 tabular-nums text-muted-foreground">{l.fabrication}</td>
                    <td className="px-4 py-3 tabular-nums text-muted-foreground">{l.expiration}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <div>{l.quantiteRestante.toLocaleString("fr-FR")}</div>
                      <div className="mt-1 h-1 w-24 ml-auto overflow-hidden rounded-full bg-white/5">
                        <div className="h-full bg-foreground/60" style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Pill_ tone={recalled ? "danger" : expired ? "neutral" : "success"}>
                        {l.statut}
                      </Pill_>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ----------------------------- DISPENSATION ----------------------------- */

function DispensationTab({ patient }: { patient: PatientRec }) {
  const [q, setQ] = useState("");
  const [onlyPatient, setOnlyPatient] = useState(true);

  const list = useMemo(() => {
    const t = q.toLowerCase();
    return dispensations
      .filter(d => !onlyPatient || d.patientId === patient.id)
      .filter(d => !t || [d.id, d.patient, d.patientId, d.medicament, d.prescripteur]
        .some(s => s.toLowerCase().includes(t)));
  }, [q, onlyPatient, patient.id]);

  const ca = list.reduce((s, d) => s + d.montant, 0);
  const remb = list.reduce((s, d) => s + d.rembCNSS, 0);
  const stup = list.filter(d => d.type === "stupéfiant").length;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Dispensations" value={String(list.length)} hint={onlyPatient ? "Patient sélectionné" : "Toutes"} />
        <Kpi label="CA dispensé" value={`${ca.toFixed(2)} DH`} hint="Toutes lignes" tone="success" />
        <Kpi label="Part CNSS" value={`${remb.toFixed(0)} DH`} hint={ca > 0 ? `${Math.round((remb / ca) * 100)}% remboursé` : "—"} />
        <Kpi label="Stupéfiants" value={String(stup)} hint="Sous contrôle" tone="warning" />
      </div>

      <Card
        title="Acte de dispensation"
        subtitle="Délivrance nominative — ordonnance, lot, posologie, CNSS."
        actions={
          <button className="inline-flex items-center gap-2 rounded-full bg-foreground px-4 h-9 text-sm font-medium text-background hover:opacity-90">
            <Plus className="h-4 w-4" /> Nouvelle dispensation
          </button>
        }
      >
        <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-3">
          <Field label="Patient">
            <input className="input-pill" value={`${patient.prenom} ${patient.nom} · ${patient.id}`} readOnly />
          </Field>
          <Field label="Prescripteur">
            <input className="input-pill" defaultValue="Dr. Andrew Smith" />
          </Field>
          <Field label="Type">
            <select className="input-pill">
              <option>Ordonnance</option>
              <option>Vente libre</option>
              <option>Stupéfiant</option>
            </select>
          </Field>
          <Field label="Médicament (CIP/DCI)">
            <input className="input-pill" placeholder="Scanner ou rechercher…" />
          </Field>
          <Field label="N° Lot">
            <select className="input-pill">
              {lots.map(l => <option key={l.id}>{l.numeroLot}</option>)}
            </select>
          </Field>
          <Field label="Quantité">
            <input className="input-pill" type="number" defaultValue={1} />
          </Field>
        </div>
      </Card>

      <Card
        title="Journal des dispensations"
        subtitle={`${list.length} acte(s) · CNSS + paiements`}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOnlyPatient(v => !v)}
              className={
                "inline-flex items-center gap-2 rounded-full px-3 h-9 text-xs " +
                (onlyPatient ? "bg-foreground text-background" : "bg-white/5 text-muted-foreground hover:text-foreground")
              }
            >
              {onlyPatient ? `Patient · ${patient.prenom} ${patient.nom}` : "Tous les patients"}
            </button>
            <div className="relative w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Patient · médicament · prescripteur"
                className="h-9 w-full rounded-full bg-white/5 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/30"
              />
            </div>
            <button className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 h-9 text-sm text-muted-foreground hover:text-foreground">
              <Download className="h-4 w-4" /> Export
            </button>
          </div>
        }
      >
        {list.length === 0 ? (
          <p className="p-10 text-center text-sm text-muted-foreground">Aucune dispensation enregistrée pour cette sélection.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3">N°</th>
                  <th className="px-4 py-3">Heure</th>
                  <th className="px-4 py-3">Patient</th>
                  <th className="px-4 py-3">Médicament</th>
                  <th className="px-4 py-3">Lot</th>
                  <th className="px-4 py-3">Prescripteur</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">Montant</th>
                  <th className="px-4 py-3 text-right">CNSS</th>
                </tr>
              </thead>
              <tbody>
                {list.map(d => (
                  <tr key={d.id} className="border-t border-white/5">
                    <td className="px-4 py-3 font-mono text-xs">{d.id}</td>
                    <td className="px-4 py-3 tabular-nums text-muted-foreground">
                      {new Date(d.date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {d.patient}
                      <div className="text-xs text-muted-foreground">{d.patientId}</div>
                    </td>
                    <td className="px-4 py-3">{d.medicament}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{d.lotId}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.prescripteur}</td>
                    <td className="px-4 py-3">
                      <Pill_ tone={d.type === "stupéfiant" ? "danger" : d.type === "ordonnance" ? "neutral" : "success"}>
                        {d.type}
                      </Pill_>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{d.montant.toFixed(2)} DH</td>
                    <td className="px-4 py-3 text-right tabular-nums text-success">{d.rembCNSS.toFixed(2)} DH</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ------------------------------ TRAÇABILITÉ ----------------------------- */

const typeIcon: Record<string, LucideIcon> = {
  fabrication: Factory,
  importation: Warehouse,
  reception: Warehouse,
  transfert: Truck,
  vente: Building2,
  retour: Truck,
  rappel: Megaphone,
  destruction: AlertTriangle,
};

function TracabiliteTab({ patient }: { patient: PatientRec }) {
  // Default to most recent lot dispensed to this patient
  const patientLots = useMemo(
    () =>
      dispensations
        .filter(d => d.patientId === patient.id)
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(d => d.lotId),
    [patient.id],
  );
  const defaultLot = patientLots[0] ?? lots[0].id;
  const [lotId, setLotId] = useState(defaultLot);
  useEffect(() => { setLotId(defaultLot); }, [defaultLot]);

  const lot = lots.find(l => l.id === lotId)!;
  const med = medicaments.find(m => m.cip === lot.cip)!;
  const events = mouvements.filter(m => m.lotId === lotId);
  const patientLotSet = new Set(patientLots);

  return (
    <div className="flex flex-col gap-5">
      <Card
        title="Track & Trace"
        subtitle={`Parcours complet d'un lot — lots du patient ${patient.prenom} ${patient.nom} en haut de liste.`}
        actions={
          <select
            value={lotId}
            onChange={e => setLotId(e.target.value)}
            className="input-pill w-[320px]"
          >
            {[...lots]
              .sort((a, b) => (patientLotSet.has(b.id) ? 1 : 0) - (patientLotSet.has(a.id) ? 1 : 0))
              .map(l => {
                const m = medicaments.find(x => x.cip === l.cip)!;
                const tag = patientLotSet.has(l.id) ? " ★ patient" : "";
                return (
                  <option key={l.id} value={l.id}>
                    {l.numeroLot} · {m.nomCommercial}{tag}
                  </option>
                );
              })}
          </select>
        }
      >
        <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-3">
          <div className="lg:col-span-2 rounded-2xl bg-white/[0.03] p-5">
            <div className="text-base font-medium">{med.nomCommercial} — {med.dosage}</div>
            <div className="text-xs text-muted-foreground">
              Lot <span className="font-mono">{lot.numeroLot}</span> · {lot.fabricant}
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat label="Fabrication" value={lot.fabrication} />
              <Stat label="Expiration" value={lot.expiration} />
              <Stat label="Qté initiale" value={lot.quantiteInitiale.toLocaleString("fr-FR")} />
              <Stat label="Qté restante" value={lot.quantiteRestante.toLocaleString("fr-FR")} />
            </div>
          </div>
          <div className="rounded-2xl bg-white/[0.03] p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Statut réglementaire</div>
            <div className="mt-3 flex items-center gap-3">
              {lot.statut === "actif" ? (
                <CheckCircle2 className="h-8 w-8 text-success" />
              ) : (
                <Megaphone className="h-8 w-8 text-destructive" />
              )}
              <div>
                <Pill_ tone={lot.statut === "actif" ? "success" : "danger"}>{lot.statut}</Pill_>
                <p className="mt-1 text-xs text-muted-foreground">CIP {lot.cip}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card title="Chaîne d'événements" subtitle={`Du fabricant au patient — ${events.length} événements enregistrés`}>
        <div className="p-5">
          {events.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Aucun événement enregistré pour ce lot.</p>
          ) : (
            <ol className="relative ml-3 space-y-6 border-l border-white/10">
              {events.map((e, i) => {
                const Icon = typeIcon[e.type] ?? Truck;
                const isLast = i === events.length - 1;
                const danger = e.type === "rappel" || e.type === "destruction";
                return (
                  <li key={e.id} className="relative pl-6">
                    <span
                      className={
                        "absolute -left-[13px] top-0 grid h-6 w-6 place-items-center rounded-full border-2 " +
                        (danger
                          ? "border-destructive bg-destructive text-destructive-foreground"
                          : isLast
                          ? "border-accent bg-accent text-accent-foreground"
                          : "border-foreground/60 bg-card text-foreground")
                      }
                    >
                      <Icon className="h-3 w-3" />
                    </span>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="mb-1 flex items-center gap-2">
                          <Pill_ tone={danger ? "danger" : "neutral"}>{e.type}</Pill_>
                          <span className="text-xs tabular-nums text-muted-foreground">
                            {new Date(e.timestamp).toLocaleString("fr-FR")}
                          </span>
                          {e.ville && <span className="text-xs text-muted-foreground">· {e.ville}</span>}
                        </div>
                        <p className="text-sm font-medium">{e.source}</p>
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span>→</span> {e.destination}
                        </p>
                      </div>
                      <span className="shrink-0 rounded-full bg-white/5 px-3 py-1 text-xs tabular-nums text-muted-foreground">
                        {e.quantite.toLocaleString("fr-FR")} {e.quantite === 1 ? "unité" : "unités"}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </Card>
    </div>
  );
}

/* ------------------------------ VUE PATIENT ----------------------------- */

function VuePatientTab({
  selected, selectedId, setSelectedId, lastId,
}: {
  selected: PatientRec;
  selectedId: string;
  setSelectedId: (id: string) => void;
  lastId: string;
}) {
  const [query, setQuery] = useState("");
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<PatientRec[] | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const hasQuery = Boolean(query.trim() || dob);

  // Simulated async search with loading + error + empty states
  useEffect(() => {
    if (!hasQuery) {
      setResults(null);
      setError(null);
      setLoading(false);
      setShowDropdown(false);
      return;
    }
    setLoading(true);
    setError(null);
    setShowDropdown(true);
    const t = setTimeout(() => {
      try {
        if (dob && !/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
          throw new Error("Date de naissance invalide.");
        }
        const q = query.trim().toLowerCase();
        const found = PATIENTS.filter(p => {
          const full = `${p.prenom} ${p.nom}`.toLowerCase();
          const matchName = !q || full.includes(q) || p.nom.toLowerCase().includes(q) || p.prenom.toLowerCase().includes(q);
          const matchDob = !dob || p.dob === dob;
          return matchName && matchDob;
        });
        setResults(found);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur de recherche.");
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => clearTimeout(t);
  }, [query, dob, hasQuery]);

  const patientDisp = dispensations
    .filter(d => d.patientId === selected.id)
    .sort((a, b) => b.date.localeCompare(a.date));
  const totalMontant = patientDisp.reduce((s, d) => s + d.montant, 0);
  const totalCNSS = patientDisp.reduce((s, d) => s + d.rembCNSS, 0);
  const isLastShown = selected.id === lastId;

  function pick(id: string) {
    setSelectedId(id);
    setShowDropdown(false);
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Search bar */}
      <div className="card-surface relative rounded-2xl p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onFocus={() => hasQuery && setShowDropdown(true)}
              placeholder="Rechercher un patient — nom, prénom…"
              className="h-11 w-full rounded-full bg-white/5 pl-11 pr-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/30"
            />
          </div>
          <div className="relative md:w-56">
            <Calendar className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="date"
              value={dob}
              onChange={e => setDob(e.target.value)}
              className="h-11 w-full rounded-full bg-white/5 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/30"
            />
          </div>
          <button
            onClick={() => pick(lastId)}
            className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 h-11 text-sm text-muted-foreground hover:text-foreground"
            title="Charger le dernier patient"
          >
            <History className="h-4 w-4" /> Dernier patient
          </button>
          {hasQuery && (
            <button
              onClick={() => { setQuery(""); setDob(""); }}
              className="rounded-full bg-white/5 px-4 h-11 text-sm text-muted-foreground hover:text-foreground"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {/* Search results dropdown */}
        {showDropdown && hasQuery && (
          <div className="absolute left-4 right-4 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-white/10 bg-card shadow-2xl">
            {loading && (
              <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Recherche en cours…
              </div>
            )}
            {!loading && error && (
              <div className="flex items-center justify-center gap-2 p-6 text-sm text-destructive">
                <XCircle className="h-4 w-4" /> {error}
              </div>
            )}
            {!loading && !error && results && results.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                Aucun patient ne correspond à « {query || dob} ».
              </div>
            )}
            {!loading && !error && results && results.length > 0 && (
              <div className="max-h-80 overflow-y-auto p-2">
                {results.map(p => (
                  <button
                    key={p.id}
                    onClick={() => pick(p.id)}
                    className={
                      "flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-colors " +
                      (p.id === selectedId ? "bg-white/[0.06]" : "hover:bg-white/5")
                    }
                  >
                    <Avatar name={`${p.prenom} ${p.nom}`} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{p.prenom} {p.nom}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.id} · {ageFrom(p.dob)} ans · {p.ville} · né(e) {new Date(p.dob).toLocaleDateString("fr-FR")}
                      </div>
                    </div>
                    {p.id === lastId && (
                      <span className="rounded-full bg-success/15 px-2 py-0.5 text-[10px] text-success">dernier</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hero patient card */}
      <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#0e0e10] via-[#141417] to-[#1a1a1f] p-6 ring-1 ring-white/5">
        <div className="absolute right-5 top-5 flex items-center gap-2">
          <button className="grid h-10 w-10 place-items-center rounded-full bg-white/5 text-foreground hover:bg-white/10">
            <Bell className="h-4 w-4" />
          </button>
          <button className="grid h-10 w-10 place-items-center rounded-full bg-foreground text-background hover:opacity-90">
            <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-wrap items-start gap-5">
          <Avatar name={`${selected.prenom} ${selected.nom}`} size="lg" />
          <div className="min-w-0 flex-1">
            {isLastShown && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-3 py-1 text-[11px] font-medium text-success">
                <span className="h-1.5 w-1.5 rounded-full bg-success" /> Dernier patient
              </span>
            )}
            <h3 className="mt-2 text-3xl font-light tracking-tight">{selected.prenom} {selected.nom}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {selected.id} · {ageFrom(selected.dob)} ans · {selected.sexe === "F" ? "Femme" : "Homme"} · Groupe {selected.groupe}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Chip icon={Calendar}>Né(e) le {new Date(selected.dob).toLocaleDateString("fr-FR")}</Chip>
              <Chip icon={MapPin}>{selected.ville}</Chip>
              <Chip icon={Phone}>{selected.tel}</Chip>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <MiniKpi label="Dispensations" value={String(patientDisp.length)} />
          <MiniKpi label="Total facturé" value={`${totalMontant.toFixed(2)} DH`} />
          <MiniKpi label="Part CNSS" value={`${totalCNSS.toFixed(0)} DH`} />
          <MiniKpi label="Dernière visite" value={patientDisp[0] ? new Date(patientDisp[0].date).toLocaleDateString("fr-FR") : "—"} />
        </div>
      </div>

      {/* Allergies / Pathologies */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface rounded-2xl p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <ShieldAlert className="h-4 w-4" /> Allergies
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {selected.allergies.length === 0
              ? <span className="text-sm text-muted-foreground">Aucune allergie connue.</span>
              : selected.allergies.map(a => <Pill_ key={a} tone="danger">{a}</Pill_>)}
          </div>
        </div>
        <div className="card-surface rounded-2xl p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <Activity className="h-4 w-4" /> Pathologies actives
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {selected.pathologies.length === 0
              ? <span className="text-sm text-muted-foreground">—</span>
              : selected.pathologies.map(p => <Pill_ key={p} tone="warning">{p}</Pill_>)}
          </div>
        </div>
      </div>

      {/* Prescriptions / dispensations */}
      <Card title="Prescriptions & dispensations" subtitle={`${patientDisp.length} actes — historique pharmaceutique`}>
        {patientDisp.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center">
            <Mail className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Aucune dispensation enregistrée pour ce patient.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 p-5 lg:grid-cols-2">
            {patientDisp.map(d => {
              const lot = lots.find(l => l.id === d.lotId);
              return (
                <div key={d.id} className="rounded-2xl bg-white/[0.03] p-4 ring-1 ring-white/5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{d.medicament}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {d.id} · {new Date(d.date).toLocaleString("fr-FR")}
                      </div>
                    </div>
                    <Pill_ tone={d.type === "stupéfiant" ? "danger" : d.type === "ordonnance" ? "neutral" : "success"}>
                      {d.type}
                    </Pill_>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                    <div>
                      <div className="text-muted-foreground">Prescripteur</div>
                      <div className="mt-0.5 font-medium">{d.prescripteur}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Lot</div>
                      <div className="mt-0.5 font-mono">{lot?.numeroLot ?? d.lotId}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-muted-foreground">Montant</div>
                      <div className="mt-0.5 tabular-nums font-medium">{d.montant.toFixed(2)} DH</div>
                      <div className="text-[11px] text-success">CNSS {d.rembCNSS.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ------------------------------ PRIMITIVES ------------------------------ */

function Avatar({ name, size = "md" }: { name: string; size?: "md" | "lg" }) {
  const initials = name.split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const cls = size === "lg" ? "h-20 w-20 text-2xl" : "h-11 w-11 text-sm";
  return (
    <div className={`grid shrink-0 place-items-center rounded-full bg-gradient-to-br from-foreground/20 to-foreground/5 font-medium text-foreground ring-1 ring-white/10 ${cls}`}>
      {initials}
    </div>
  );
}

function Chip({ icon: Icon, children }: { icon: LucideIcon; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs text-foreground/80">
      <Icon className="h-3 w-3" /> {children}
    </span>
  );
}

function MiniKpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-white/[0.04] p-4 ring-1 ring-white/5">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-light tabular-nums">{value}</div>
    </div>
  );
}

function Card({
  title, subtitle, actions, children,
}: { title: string; subtitle?: string; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="card-surface overflow-hidden rounded-2xl">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-white/5 p-5">
        <div>
          <div className="text-base font-medium">{title}</div>
          {subtitle && <div className="mt-0.5 text-xs text-muted-foreground">{subtitle}</div>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

function Kpi({ label, value, hint, tone = "neutral" }:
  { label: string; value: string; hint?: string; tone?: "neutral" | "success" | "warning" | "danger" }) {
  const ring =
    tone === "success" ? "ring-success/30" :
    tone === "warning" ? "ring-warning/30" :
    tone === "danger" ? "ring-destructive/30" : "ring-white/10";
  return (
    <div className={`card-surface rounded-2xl p-5 ring-1 ${ring}`}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-2 text-3xl font-light tracking-tight">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function Pill_({
  children, tone = "neutral",
}: { children: React.ReactNode; tone?: "neutral" | "success" | "warning" | "danger" }) {
  const cls =
    tone === "success" ? "bg-success/15 text-success border-success/30" :
    tone === "warning" ? "bg-warning/15 text-warning border-warning/30" :
    tone === "danger" ? "bg-destructive/15 text-destructive border-destructive/30" :
    "bg-white/5 text-foreground/80 border-white/10";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {children}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium tabular-nums">{value}</div>
    </div>
  );
}

/* Mail icon used in empty state */
function Mail(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}
